import {
  Component,
  inject,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
  viewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '@app/components/base/base.component';
import { MenusServices } from '@app/services/menus.service';
import { NotificationService } from '@app/services/notifications.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  coerceOpenQuoteBoolean,
  statusType,
  normalizeOpenQuoteStatusForUi,
  isOpenQuoteFullyTerminal,
  isOpenQuoteInReview,
  isOpenQuoteStatusCompleted,
  isOpenQuoteClientSectionReadOnly,
  isOpenQuoteFinanceAndDealsReadOnly,
  OpenQuoteRouteMode,
  resolveOpenQuoteRouteMode,
} from './model/open-quote.model';
import { AuthService } from '@app/common/auth.service';
import { OpenQuoteService } from '@app/pages/quote/open-quote/services/open-quote-service.service';
import { ModalService } from '@app/components/modal/modal.service';
import { UsersService } from '@app/pages/users/services/users.service';
import { encodingBase64 } from '@app/components/base/encodingB64';
import { quoteEnum } from '@app/components/grace-special-payments/quote-enum.model';
import { ResponsiveService } from '@app/services/responsive.service';
import { ScrollHotkeysService } from '@app/services/scrollHotkeys.service';
import { EMPTY, finalize, interval, of } from 'rxjs';
import { createLoadingTracker } from '@app/core/util/loading-tracker';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ClientsService } from '@app/pages/clients/services/clients.service';
import { PersonType } from '@app/pages/clients/interfaces/client';
import { environment } from '@env/environment.local';
import { ParserService } from '@app/services/parser.service';
import { HttpErrorResponse } from '@angular/common/http';
import { NavigationBackService } from '@app/services/navigation-back.service';
import { DealerBranchCatalogService } from '@app/pages/quote/open-quote/services/dealer-branch-catalog.service';

const UPM = 'upm';
const BASE_INFORMATION = 'baseInformation';
/** Título del header cuando la cotización todavía no tiene nombre (nueva). */
const DEFAULT_HEADER_TITLE = 'Cotizador comercial';
/** Listado de cotizaciones comerciales: destino si no hay historial interno. */
export const OPEN_QUOTE_LIST_ROUTE = '/quote/open-quote';
/**
 * El backend responde 404 tanto para una cotización inexistente como para
 * una que el usuario no puede ver, así que el mensaje cubre los dos casos.
 */
export const QUOTE_NOT_FOUND_MESSAGE =
  'La cotización solicitada no existe o no tiene permisos para consultarla.';
/**
 * Se explica el motivo en lugar de apagar el botón: un "Guardar"
 * deshabilitado sin texto deja al usuario sin saber qué corregir.
 */
export const DEALER_BRANCH_MISMATCH_MESSAGE =
  'La sucursal seleccionada no pertenece al distribuidor de la cotización. ' +
  'Se limpió el campo: elija una sucursal válida y vuelva a guardar.';
@Component({
  selector: 'app-new-open-quote',
  templateUrl: './new-open-quote.component.html',
  styleUrls: ['./new-open-quote.component.scss'],
})
export class NewOpenQuoteComponent extends BaseComponent implements OnInit {
  @ViewChild('dealsCmp') dealsCmp: any;
  readonly extendValidityModalTemplate = viewChild<TemplateRef<unknown>>('extendValidityModalTemplate');
  extendValidityModal = {
    title: 'Extender vigencia',
    subtitle: '',
    btn1: 'Cancelar',
    btn2: 'Confirmar',
  };
  quoteType: quoteEnum = quoteEnum.openQuote;
  readonly loading = createLoadingTracker();
  form: FormGroup;
  isModeEdition: boolean = false;
  /** Completado / Aprobado / En revisión: cabecera financiera y propuestas solo lectura. */
  readonlyFinanceAndDeals = false;
  /** Cliente solo lectura salvo dueño en Completado (solo cliente editable). */
  readonlyClientSection = false;
  /** Si la quote está vinculada a una solicitud de crédito: bloquear toda edición (incluye cliente). */
  linkedToCreditRequest = false;
  /** Si se ingresó por “Ver” (ojo): forzar solo lectura aunque el status permitiera editar cliente. */
  forceReadOnlyView = false;
  quoteFullyTerminal = false;
  quoteInReview = false;
  showApproveQuoteButton = false;
  quote: any = {};
  id: any = 0;
  roRacGlobal: any;
  proposalAmount: number = 0;
  faTotal: number;
  updatedDate: string;
  creationDate: string;
  deals: any[] = [];
  customer: any;
  readonly financialService = signal<any>(null);
  statusType = statusType;
  isValid: boolean = false;
  userId: string;
  userName: string;
  customerId: number | null = null;
  isSelectedDeal: boolean = false;
  _quote: any;
  valorOriginal: string;
  activeClientCatalog = environment.active_client_catalog;
  readonly responsive = inject(ResponsiveService);
  readonly scrollHotkeys = inject(ScrollHotkeysService);
  public readonly clientsService = inject(ClientsService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly navigationBack = inject(NavigationBackService);
  private readonly dealerBranchCatalog = inject(DealerBranchCatalogService);
  readonly quoteSignal = signal<any>(null);
  /**
   * Header invertido: subtítulo (acción + ID) arriba, nombre de la cotización
   * como título. El tag "Vigente hasta" sigue el mismo flag que el resto de
   * la UI de vigencia (`showQuoteValidityListFilter`).
   */
  readonly config = signal({
    invertTitle: true,
    activeDetails: false,
    activeHistory: false,
    activeCustomTag: environment.showQuoteValidityListFilter,
  });
  readonly changedEvent = signal<any>({ value: "", key: "", group: "", modalTitle: "", modalMessage: "", btnCancel: "", btnSave: "" });

  quoteValidityInfo: {
    valid?: boolean;
    minQuoteValidityDays?: number;
    validityEndDate?: string;
    validityExtensionApplied?: boolean;
    hasInactiveFinancialSetup?: boolean;
  } | null = null;
  extendingValidity = false;
  readonly showQuoteValidityUi = environment.showQuoteValidityListFilter;

  get isLoading(): boolean {
    return this.loading.count() > 0;
  }

  constructor(
    route: ActivatedRoute,
    menu: MenusServices,
    notification: NotificationService,
    private quoteService: OpenQuoteService,
    private fb: FormBuilder,
    private router: Router,
    public authService: AuthService,
    private usersService: UsersService,
    private modalService: ModalService,
  ) {
    super(route, menu, notification);
    this.id = route.snapshot.paramMap.get('id');
    this.forceReadOnlyView =
      route.snapshot.queryParamMap.get('view') === 'readonly' ||
      route.snapshot.queryParamMap.get('readonly') === 'true';
    this.sendDataToMenu();
    /* const userId = window.sessionStorage.getItem('userId');
    this.usersService.getUserById(userId).subscribe({
      next: (response) => {
        window.sessionStorage.setItem(UPM, encodingBase64(response.data.email));
      },
      error: (error) => { },
    });*/
  }

  ngOnInit() {
    this.responsive.onListen();
    // Mantener en sync por si navegan en la misma ruta con distintos query params.
    this.activatedRoute.queryParamMap
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((qp) => {
        this.forceReadOnlyView =
          qp.get('view') === 'readonly' || qp.get('readonly') === 'true';
      });
    this.createForm();
    const mode = resolveOpenQuoteRouteMode(this.id);
    if (mode === OpenQuoteRouteMode.Invalid) {
      // `/edit-quote-open/abc`: antes caía al alta y mostraba un formulario
      // de cotización nueva sobre una URL de edición.
      this.leaveQuoteNotFound();
      return;
    }
    if (mode === OpenQuoteRouteMode.Existing) {
      this.getQuoteById();
    }
    // interval(10000).subscribe(() => {
    //   console.log(this.form.getRawValue());
    //   for (const key in this.form.controls) {
    //     const control = this.form.controls[key];
    //     if (control.invalid) {
    //       console.log(`El campo ${key} tiene el siguiente error:`, control.errors);
    //     }
    //   }
    // });
  }

  createForm() {
    this.form = this.fb.group({
      financialService: this.fb.group({
        companyName: ['', [Validators.required]],
        quoteName: ['', [Validators.required]],
        quoteType: [this.quoteType, []],
        daysOfValidity: [15, [Validators.required]],
        dealerId: [, [Validators.required]],
        dealerBranchId: [, [Validators.required]],
        statusType: [statusType.inProgress, []],
      }),
      customer: ['', [Validators.required]],
      simulations: ['', [Validators.required]],
      comments: ['', []],
      customerId: [, []],
    });
    this.form.valueChanges.subscribe((value) => {
      this.isValid =
        this.form.controls['financialService'].valid &&
        this.form.controls['customer'].valid;
    });
    return of(EMPTY);
  }

  /** Misma regla que OD (`canExtendQuoteValidityInCreditRequest`). */
  showExtendValidityBanner(): boolean {
    if (!this.showQuoteValidityUi || !(Number(this.id) > 0)) {
      return false;
    }
    if (
      this.userId == null ||
      String(this.userId) !== String(this.authService.currentUserIds)
    ) {
      return false;
    }
    if (!this.isExpiredByValidity(this.quoteValidityInfo)) {
      return false;
    }
    // OD: extendValidityDaysForDisplay() ?? 30 → siempre hay días.
    return this.resolveOpenQuoteValidityDays() != null;
  }

  private resolveOpenQuoteValidityDays(): number | null {
    const value = this.quoteValidityInfo?.minQuoteValidityDays;
    if (typeof value === 'number' && value > 0) {
      return value;
    }
    if (typeof value === 'string' && Number(value) > 0) {
      return Number(value);
    }
    return 30;
  }

  private isExpiredByValidity(source?: {
    valid?: boolean | null;
    validityEndDate?: string | Date | null;
  } | null): boolean {
    if (!source) {
      return false;
    }
    const end = source.validityEndDate;
    if (end != null && end !== '') {
      const t = new Date(end as string | Date).getTime();
      return !Number.isNaN(t) && t < Date.now();
    }
    return source.valid === false;
  }

  private formatExtendedValidityDate(
    validityEndDate: string | Date | null | undefined,
    days: number,
  ): string {
    if (!validityEndDate) {
      return '';
    }
    const date = new Date(validityEndDate);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const baseDate = date.getTime() < Date.now() ? new Date() : date;
    baseDate.setUTCDate(baseDate.getUTCDate() + days);
    return baseDate.toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }

  openExtendValidityConfirmModal(): void {
    const modalTemplate = this.extendValidityModalTemplate();
    if (!modalTemplate) {
      return;
    }
    const days = this.resolveOpenQuoteValidityDays() ?? 30;
    const extendedDate = this.formatExtendedValidityDate(
      this.quoteValidityInfo?.validityEndDate,
      days,
    );
    this.extendValidityModal.subtitle = extendedDate
      ? `Cotización vencida. ¿Seguro que desea extender la vigencia de esta cotización hasta el ${extendedDate} (${days} días)?`
      : `Cotización vencida. ¿Seguro que desea extender la vigencia de esta cotización por ${days} días?`;
    this.modalService.component = modalTemplate;
    this.modalService.openModal();
  }

  closeExtendValidityConfirmModal(): void {
    this.modalService.closeModal();
  }

  confirmExtendValidityFromModal(): void {
    this.modalService.closeModal();
    this.extendValidityFromDetail();
  }

  extendValidityFromDetail(): void {
    const quoteId = Number(this.id);
    if (!Number.isFinite(quoteId) || quoteId <= 0 || this.extendingValidity) {
      return;
    }
    this.extendingValidity = true;
    this.quoteService.extendOpenQuoteValidity(quoteId).pipe(
      finalize(() => {
        this.extendingValidity = false;
      }),
      takeUntilDestroyed(this._destroyRef)
    ).subscribe({
      next: (response) => {
        if (response?.data) {
          this.applyValidityExtensionToLocalState(response.data);
        }
        this.quoteService.deleteKeyCache();
        this.showNotificationSuccess('Vigencia extendida correctamente.');
      },
      error: (error) => {
        const msg =
          error?.error?.errors?.[0] ??
          'No se pudo extender la vigencia de la cotización.';
        this.showNotificationError(typeof msg === 'string' ? msg : String(msg));
      },
    });
  }

  private applyValidityExtensionToLocalState(payload: {
    valid?: boolean;
    minQuoteValidityDays?: number;
    validityEndDate?: string | Date;
    validityExtensionApplied?: boolean;
    hasInactiveFinancialSetup?: boolean;
  }): void {
    this.quoteValidityInfo = {
      valid: payload.valid,
      minQuoteValidityDays: payload.minQuoteValidityDays,
      validityEndDate: this.toIsoDateString(payload.validityEndDate),
      validityExtensionApplied: payload.validityExtensionApplied ?? true,
      hasInactiveFinancialSetup: payload.hasInactiveFinancialSetup,
    };
    if (this._quote && typeof this._quote === 'object') {
      Object.assign(this._quote, {
        valid: payload.valid,
        minQuoteValidityDays: payload.minQuoteValidityDays,
        validityEndDate: payload.validityEndDate,
        validityExtensionApplied: payload.validityExtensionApplied ?? true,
        hasInactiveFinancialSetup: payload.hasInactiveFinancialSetup,
      });
    }
    this.quote = { ...this.quote, ...this._quote };
    this.quoteSignal.set(this.quote);
  }

  private toIsoDateString(value: string | Date | undefined | null): string | undefined {
    if (value == null) return undefined;
    if (typeof value === 'string') return value;
    try {
      return (value as Date).toISOString();
    } catch {
      return String(value);
    }
  }

  private syncQuoteValidityFromDetailResponse(data: any): void {
    if (!data) {
      this.quoteValidityInfo = null;
      return;
    }
    this.quoteValidityInfo = {
      valid: data.valid,
      minQuoteValidityDays: data.minQuoteValidityDays ?? data.daysOfValidity,
      validityEndDate: this.toIsoDateString(data.validityEndDate),
      validityExtensionApplied: data.validityExtensionApplied,
      hasInactiveFinancialSetup: data.hasInactiveFinancialSetup,
    };
  }

  getQuoteById() {
    this.loading.track(this.quoteService.getQuoteOpenById(this.id)).pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (response) => {
        this.quote = response.data;
        this.quoteSignal.set(response.data);
        this._quote = response.data;
        this.setValue(response.data);
        this.syncQuoteValidityFromDetailResponse(response.data);
        const rawStatus = response.data?.status;
        this.readonlyFinanceAndDeals =
          isOpenQuoteFinanceAndDealsReadOnly(rawStatus);
        this.readonlyClientSection = isOpenQuoteClientSectionReadOnly(
          rawStatus,
          response.data?.userId,
          this.authService.currentUserIds
        );
        this.quoteFullyTerminal = isOpenQuoteFullyTerminal(rawStatus);
        this.quoteInReview = isOpenQuoteInReview(rawStatus);
        this.showApproveQuoteButton =
          this.quoteInReview &&
          String(this.userId) === String(this.authService.currentUserIds);
        this.validateCustomerForm();
        this.applyOpenQuoteCreditLinkFromPayload(response.data);
        this.applyForceReadOnlyView();
      },
      error: (error) => this.handleLoadQuoteError(error),
    });
  }

  /**
   * 404: la cotización no existe o no es visible para este usuario (el
   * backend no distingue los dos casos). No hay nada que mostrar, así que
   * se avisa y se lo saca de la pantalla.
   *
   * 401/403 NO se tocan acá a propósito: son territorio del
   * `HttpErrorInterceptor` (refresh de token y, si falla, logout). Si el
   * componente además navegara, pelearía con esa navegación.
   *
   * Cualquier otro error (500, red) se notifica pero lo deja donde está
   * para que pueda reintentar.
   */
  private handleLoadQuoteError(error: HttpErrorResponse | unknown): void {
    if ((error as HttpErrorResponse)?.status === 404) {
      this.leaveQuoteNotFound();
      return;
    }
    this.showNotificationError(this.msgBadRequest);
  }

  /** Avisa el motivo y vuelve a donde estaba (o al listado). */
  private leaveQuoteNotFound(): void {
    this.showNotificationError(QUOTE_NOT_FOUND_MESSAGE);
    this.navigationBack.backOrFallback(OPEN_QUOTE_LIST_ROUTE);
  }

  private applyForceReadOnlyView(): void {
    if (!this.forceReadOnlyView) return;
    // Forzar solo lectura total (incl. cotización en estados no editables).
    this.readonlyFinanceAndDeals = true;
    this.readonlyClientSection = true;
    this.quoteFullyTerminal = true;
    this.showApproveQuoteButton = false;
    try {
      Object.values(this.form?.controls ?? {}).forEach((c: any) => c?.disable?.());
    } catch {
      // noop
    }
  }

  /** Usa `linkedToCreditRequest` del quote-service; ya no se llama a credit-request-service. */
  private applyOpenQuoteCreditLinkFromPayload(data: any | null | undefined): void {
    if (!data) {
      this.linkedToCreditRequest = false;
      return;
    }
    const linked = Boolean(data.linkedToCreditRequest);
    this.linkedToCreditRequest = linked;
    if (linked) {
      this.readonlyFinanceAndDeals = true;
      this.readonlyClientSection = true;
      this.quoteFullyTerminal = true;
      try {
        Object.values(this.form?.controls ?? {}).forEach((c: any) => c?.disable?.());
      } catch {
        // noop
      }
    }
  }

  setValue(response: any) {
    this.roRacGlobal = response['roRac'];
    this.proposalAmount = response['proposalAmount'];
    this.faTotal = response['faTotal'];
    this.creationDate = response['creationDate'];
    this.updatedDate = response['updatedDate'];
    this.deals = response['openQuoteDeals'];
    const rawCustomer = response['customer'] ?? response['customerOpenQuote'];
    const customerNormalized = this.normalizeOpenQuoteCustomer(rawCustomer);
    this.customer = customerNormalized;
    this.customerId = response['customerId'];
    this.form.controls['customerId'].setValue(response['customerId']);
    this.form.controls['customer'].setValue(customerNormalized);
    this.userId = response['userId'];
    this.userName = response['userName'];
    this.form.controls['comments'].setValue(response['comments']);
    this.financialService.set({
      companyName: response['companyName'],
      quoteName: response['quoteName'],
      daysOfValidity: response['daysOfValidity'],
      dealerId: response['dealerId'],
      dealerBranchId: response['dealerBranchId'],
      statusType: normalizeOpenQuoteStatusForUi(response['status']),
    });
    if (this.userId != this.authService.currentUserIds) {
      this.form.controls['comments'].disable();
    }
    this.isSelectedDeal = this.deals.some((deal) => deal.isSelected == true);
  }

  /** Datos de cliente de la cotización abierta (`customer` en JSON = CustomerOpenQuote). */
  private normalizeOpenQuoteCustomer(raw: any): any {
    if (!raw || typeof raw !== 'object') {
      return raw;
    }
    return {
      ...raw,
      feriaCustomer: coerceOpenQuoteBoolean(raw['feriaCustomer']),
    };
  }

  verifySelectedDeal() {
    this.isSelectedDeal = this.deals.some((deal) => deal.isSelected == true);
  }

  validateCustomerForm() {
    const customer = this.form.get('customer')?.value ?? this.customer;
    if(customer !== null && customer?.rfc !== null && this.activeClientCatalog){
      this.clientsService.searchClientByRFC(customer.rfc).pipe(takeUntilDestroyed(this._destroyRef)).subscribe((res)=>{
        const merged = {
          ...customer,
          ...res.data,
          feriaCustomer: coerceOpenQuoteBoolean(customer?.feriaCustomer),
          isFromProLeaseNet: res.data.isFromProLeaseNet,
        };
        const resolved =
          this.clientsService.resolveNaturalPersonNameFields(merged);
        const normalized =
          merged.personType === PersonType.NATURAL_PERSON
            ? {
                ...merged,
                ...resolved,
                name: resolved.firstName,
                customerName: null,
              }
            : {
                ...merged,
                name: merged.customerName || merged.firstName,
              };
        this.form.controls['customer'].setValue(normalized);
        this.customer = this.form.get('customer')?.value;
      });
    }
  }

  generateQuote() {
    this.loading.track(this.quoteService.generateQuoteOpen()).pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (response) => {
      },
      error: (error) => {
      },
    });
  }

  /**
   * Salvaguarda de último momento: la sucursal tiene que seguir siendo del
   * distribuidor seleccionado.
   *
   * La sincronía del formulario ya debería garantizarlo; esto existe para
   * que un par incoherente NUNCA llegue al backend, y para que el usuario
   * vea qué pasó en vez de descubrirlo después en la cotización guardada.
   */
  private ensureDealerBranchCoherence(): boolean {
    const dealerId = this.form.get('financialService.dealerId')?.value;
    const branchControl = this.form.get('financialService.dealerBranchId');
    if (this.dealerBranchCatalog.isPairCoherent(dealerId, branchControl?.value)) {
      return true;
    }
    branchControl?.setValue(null);
    branchControl?.markAsTouched();
    this.showNotificationError(DEALER_BRANCH_MISMATCH_MESSAGE);
    return false;
  }

  saveQuote(deals: any) {
    if (!this.ensureDealerBranchCoherence()) {
      return;
    }
    if (this.id > 0) {
      this.updateQuote(deals);
      this.isSelectedDeal = this.deals.some((deal) => deal.isSelected == true);
    } else {
      this.createDraft(deals);
    }
  }

  createDraft(deals: any) {
    const model = this.getModelDraft(deals);
    this.loading.track(this.quoteService.saveQuoteOpenDraft(model)).pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (response) => {
        this.quoteService.deleteKeyCache();
        this.quote = response.data;
        this.quoteSignal.set(response.data);
        this.id = response.data['id'];
        this.roRacGlobal = response.data['roRac'];
        this.proposalAmount = response.data['proposalAmount'];
        this.faTotal = response.data['faTotal'];
        this.creationDate = response.data['creationDate'];
        this.updatedDate = response.data['updatedDate'];
        this.deals = response.data['openQuoteDeals'];
        this.quoteService.lastDeal.next(response.data['openQuoteDeals']);
        this.userId = response.data['userId'];
        this.syncFinancialServiceFromFormOrResponse(response.data);
      },
      error: (error) => {
        this.showNotificationError(this.msgBadRequest);
      },
    });
  }

  /** Mantiene el signal alineado con el formulario tras el primer guardado (p. ej. al crear una propuesta). */
  private syncFinancialServiceFromFormOrResponse(data?: any): void {
    const fs = this.form.get('financialService');
    if (!fs) {
      return;
    }
    this.financialService.set({
      companyName: data?.companyName ?? fs.get('companyName')?.value,
      quoteName: data?.quoteName ?? fs.get('quoteName')?.value,
      daysOfValidity: data?.daysOfValidity ?? fs.get('daysOfValidity')?.value,
      dealerId: data?.dealerId ?? fs.get('dealerId')?.value,
      dealerBranchId: data?.dealerBranchId ?? fs.get('dealerBranchId')?.value,
      statusType: normalizeOpenQuoteStatusForUi(
        data?.status ?? fs.get('statusType')?.value
      ),
    });
  }

  updateQuote(deals: any, downloadPdf?: boolean) {
    let isSelectedDeal = this.deals.some((deal) => deal.isSelected == true);
    // En modo "Ver" o cuando está vinculada a Solicitud de Crédito:
    // permitir Descargar PDF, pero bloquear cualquier edición.
    if (downloadPdf && (this.forceReadOnlyView || this.linkedToCreditRequest)) {
      if (!isSelectedDeal) {
        this.showNotificationError('Es necesario seleccionar al menos una propuesta.');
        return;
      }
      this.downloadPdf();
      return;
    }
    if (this.forceReadOnlyView || this.linkedToCreditRequest) {
      this.showNotificationError('Esta cotización está vinculada a una solicitud de crédito y no se puede editar.');
      return;
    }
    if (downloadPdf) {
      if (!isSelectedDeal) {
        this.showNotificationError(
          'Es necesario seleccionar al menos una propuesta.'
        );
        return;
      }
      if (this.quoteFullyTerminal) {
        this.downloadPdf();
        return;
      }
    }

    let model = this.getModelUpdate(deals);
    if (this.userId != null && this.userId != this.authService.currentUserIds) {
      if (downloadPdf) this.downloadPdf();
      return;
    }

    this.loading.track(this.quoteService.updateQuoteOpen(this.id, model)).pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (response) => {
        this.quoteService.deleteKeyCache();
        this.quote = response.data;
        this.quoteSignal.set(response.data);
        this.id = response.data['id'];
        this.roRacGlobal = response.data['roRac'];
        this.proposalAmount = response.data['proposalAmount'];
        this.faTotal = response.data['faTotal'];
        this.creationDate = response.data['creationDate'];
        this.updatedDate = response.data['updatedDate'];
        this.deals = response.data['openQuoteDeals'];
        this.userId = response.data['userId'];
        this._quote = response.data;
        this.syncQuoteValidityFromDetailResponse(response.data);
        // this.isLoading = false;
        if (downloadPdf) this.downloadPdf();
      },
      error: (error) => {
        // this.isLoading = false;
        this.showNotificationError(this.msgBadRequest);
      },
    });
  }

  getModelDraft(deals: any) {
    const customerForm = this.form.get('customer')?.value;
    const customerId = this.form.get('customerId')?.value;
    const activeClientWithRFC = this.activeClientCatalog && !!customerId;
    const customerDto: any = activeClientWithRFC ? {
      dfsRating: customerForm.dfsRating,
      customerType: customerForm.customerType,
      personType: customerForm.personType,
      feriaCustomer: coerceOpenQuoteBoolean(customerForm?.feriaCustomer),
    } : this.normalizeOpenQuoteCustomer(customerForm);
    if (!environment.featureLoyaltyFeriaQuotation) {
      delete customerDto.feriaCustomer;
    }
    let model = {
      dealerId: this.form.get('financialService.dealerId')?.value,
      dealerBranchId: this.form.get('financialService.dealerBranchId')?.value,
      companyName: this.form.get('financialService.companyName')?.value,
      status: statusType.inProgress,
      quoteName: this.form.get('financialService.quoteName')?.value,
      daysOfValidity: this.form.get('financialService.daysOfValidity')?.value,
      customer: customerDto,
      customerId: activeClientWithRFC ? customerId : null,
      openQuoteDeals: deals,
      comments: this.form.get('comments')?.value,
    };
    return model;
  }

  getModelUpdate(deals: any, status?: string) {
    const customerForm = this.form.get('customer')?.value;
    const customerId = this.form.get('customerId')?.value;
    const customerDto: any = customerId ? {
      dfsRating: customerForm.dfsRating,
      customerType: customerForm.customerType,
      personType: customerForm.personType,
      feriaCustomer: coerceOpenQuoteBoolean(customerForm?.feriaCustomer),
    } : this.normalizeOpenQuoteCustomer(customerForm);
    if (!environment.featureLoyaltyFeriaQuotation) {
      delete customerDto.feriaCustomer;
    }
    let model = {
      id: this.id,
      creatioDate: this.creationDate,
      userId: this.userId,
      dealerId: this.form.get('financialService.dealerId')?.value,
      dealerBranchId: this.form.get('financialService.dealerBranchId')?.value,
      companyName: this.form.get('financialService.companyName')?.value,
      quoteName: this.form.get('financialService.quoteName')?.value,
      status:
        status ??
        normalizeOpenQuoteStatusForUi(this._quote?.status) ??
        statusType.inProgress,
      daysOfValidity: this.form.get('financialService.daysOfValidity')?.value,
      proposalAmount: this.proposalAmount,
      roRac: this.roRacGlobal,
      updatedDate: this.updatedDate,
      faTotal: this.faTotal,
      customer: customerDto,
      customerId: customerId,
      openQuoteDeals: deals,
      comments: this.form.get('comments')?.value,
    };
    return model;
  }

  changeMode(e: any) {
    let model = this.getModelUpdate(this.deals, statusType.InReview);
    window.sessionStorage.setItem(BASE_INFORMATION, JSON.stringify(model));
    this.isModeEdition = !this.isModeEdition;
  }

  save() {
    if (this.forceReadOnlyView || this.linkedToCreditRequest) {
      this.showNotificationError('Esta cotización está vinculada a una solicitud de crédito y no se puede editar.');
      return;
    }
    if (!this.ensureDealerBranchCoherence()) {
      return;
    }
    let model = this.getModelUpdate(
      this.deals,
      normalizeOpenQuoteStatusForUi(
        this.form.get('financialService.statusType')?.value
      )
    );
    this.loading.track(this.quoteService.updateQuoteOpen(this.id, model)).pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (response) => {
        this.quoteService.deleteKeyCache();
        if (response?.data) {
          this._quote = response.data;
          this.quote = response.data;
          this.quoteSignal.set(response.data);
          this.syncFinancialServiceFromFormOrResponse(response.data);
          const rs = response.data?.status;
          this.readonlyFinanceAndDeals = isOpenQuoteFinanceAndDealsReadOnly(rs);
          this.readonlyClientSection = isOpenQuoteClientSectionReadOnly(
            rs,
            response.data?.userId,
            this.authService.currentUserIds
          );
          this.quoteFullyTerminal = isOpenQuoteFullyTerminal(rs);
          this.quoteInReview = isOpenQuoteInReview(rs);
          this.showApproveQuoteButton =
            this.quoteInReview &&
            String(this.userId) === String(this.authService.currentUserIds);
        }
        this.showNotificationSuccess(
          'La cotización se ha guardado correctamente.'
        );
      },
      error: (error) => {
        this.showNotificationError(
          'Ha ocurrido un error al momento de guardar la cotización.'
        );
      },
    });
  }

  approveQuote() {
    if (this.forceReadOnlyView || this.linkedToCreditRequest) {
      this.showNotificationError('Esta cotización está vinculada a una solicitud de crédito y no se puede editar.');
      return;
    }
    if (!this.ensureDealerBranchCoherence()) {
      return;
    }
    const model = this.getModelUpdate(this.deals, statusType.approved);
    this.loading.track(this.quoteService.updateQuoteOpen(this.id, model)).pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (response) => {
        this.quoteService.deleteKeyCache();
        this.quote = response.data;
        this.quoteSignal.set(response.data);
        this._quote = response.data;
        this.setValue(response.data);
        this.syncQuoteValidityFromDetailResponse(response.data);
        const rawStatus = response.data?.status;
        this.readonlyFinanceAndDeals =
          isOpenQuoteFinanceAndDealsReadOnly(rawStatus);
        this.readonlyClientSection = isOpenQuoteClientSectionReadOnly(
          rawStatus,
          response.data?.userId,
          this.authService.currentUserIds
        );
        this.quoteFullyTerminal = isOpenQuoteFullyTerminal(rawStatus);
        this.quoteInReview = isOpenQuoteInReview(rawStatus);
        this.showApproveQuoteButton =
          this.quoteInReview &&
          String(this.userId) === String(this.authService.currentUserIds);
        this.showNotificationSuccess('La cotización ha sido aprobada.');
      },
      error: () => {
        this.showNotificationError(
          'No fue posible aprobar la cotización. Verifique permisos y estado.'
        );
      },
    });
  }

  cancel() {
    this.router.navigate(['/quote/open-quote']);
  }

  withoutSpecialCharacters(event: any) {
    const reg = new RegExp(/^[\wáéíóú()@.,;?¿:¡!$%+*=\-_\ \t\n]$/);
    if (!reg.test(event.key)) {
      event.preventDefault();
    }
  }

  downloadPdf() {
    this.loading.track(this.quoteService.downloadPdf(this.id)).pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (blob: Blob) => {
        this.saveAsPdf(blob, `Cotizacion_${this.id}`);
      },
      error: (blob: Blob) => {
        this.showNotificationError(
          'Ha ocurrido un error no se pudo descargar pdf.'
        );
      },
    });
  }

  showBtn() {
    if (this.id) {
      if (this.userId != this.authService.currentUserIds) {
        return false;
      } else {
        return true;
      }
    }
    return true;
  }

  isEditingMode(): boolean {
    return this.deals.length > 0 && this._quote;
  }

  /** Guardar: edición normal, En revisión→Aprobado, o solo cliente en Completado. */
  get showSaveButton(): boolean {
    if (!this.readonlyFinanceAndDeals) {
      return true;
    }
    const isOwner =
      String(this.userId) === String(this.authService.currentUserIds);
    if (isOpenQuoteInReview(this._quote?.status) && isOwner) {
      return true;
    }
    return isOpenQuoteStatusCompleted(this._quote?.status) && isOwner;
  }

  handleEditingStarted(valor: string) {
    this.valorOriginal = valor;
  }

  handleSelectedCustomerId(id: number) {
    this.form.controls['customerId'].setValue(id);
  }

  /** Nombre de la cotización; para una nueva (sin nombre) cae al título genérico. */
  getTitle(): string {
    if (this.isNewQuote()) {
      return DEFAULT_HEADER_TITLE;
    }
    const name = this.quoteSignal()?.quoteName;
    return typeof name === 'string' && name.trim() !== ''
      ? name
      : DEFAULT_HEADER_TITLE;
  }

  getSubtitle(): string {
    if (this.isNewQuote()) {
      return 'Nueva cotización comercial';
    }
    const isOwner = this.userId == this.authService.currentUserIds;
    if (!isOwner) {
      return `Ver cotización comercial ${this.id}`;
    }
    if (this.quoteInReview) {
      return `Cotización comercial ${this.id} en revisión`;
    }
    if (isOpenQuoteStatusCompleted(this._quote?.status)) {
      return `Editar cliente, cotización comercial ${this.id}`;
    }
    return this.readonlyFinanceAndDeals && this.readonlyClientSection
      ? `Ver cotización comercial ${this.id}`
      : `Editar cotización comercial ${this.id}`;
  }

  /** `customTag` del header: "Vigente hasta dd-MMM-yyyy" o vacío si no hay fecha. */
  getProgressDetails(): { customTag: string } {
    const end = this.quoteValidityInfo?.validityEndDate;
    const formatted = end ? ParserService.formatDateToShort(end) : '';
    return { customTag: formatted ? `Vigente hasta ${formatted}` : '' };
  }

  private isNewQuote(): boolean {
    return this.id == 0 || this.id == null;
  }

  get formCustomerId(): number | null {
    return this.form.get('customerId')?.value;
  }
}
